import React, { useState, useEffect, useRef } from 'react'
import { useMediaQuery } from 'react-responsive'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSelector } from "react-redux";
import NavbarMob from '../components/NavbarMob'
import Navbar from '../components/Navbar'
import FooterMob from '../components/FooterMob'
import Footer from '../components/Footer'
import filter from '../images/Products/filter.png'
import refreshimg from '../images/Products/refresh.png'
import product1 from '../images/Home/product2.webp'
import heart from '../images/Home/heart.png'
import filledHeart from '../images/Home/heart2.png';
import forward from '../images/Products/forwardpng.png'
import top from '../images/Products/top.png'
import Filter from '../components/Filter'
import axios from 'axios'

function SearchPage() {
    const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
    const isTab = useMediaQuery({ query: '(max-width: 1024px)' });
    const [colors, setColors] = useState([]);
    const [sizes, setSizes] = useState([]);
    const [loginId, setLoginId] = useState(null);

    // Optimized states from Home page technique
    const [allProducts, setAllProducts] = useState([]);
    const [visibleProducts, setVisibleProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [targetProductCount] = useState(100);

    // Filter states
    const [minPrice, setMinPrice] = useState(70);
    const [maxPrice, setMaxPrice] = useState(600);
    const [showFilter, setShowFilter] = useState(false);
    const [isPriceExpanded, setPriceExpanded] = useState(true);
    const [isColorsExpanded, setIsColorsExpanded] = useState(true);
    const [isSizeExpanded, setIsSizeExpanded] = useState(true);
    const [isDressExpanded, setIsDressExpanded] = useState(true);
    const [showFilterMob, setShowFilterMob] = useState(false);
    const location = useLocation();
    const [selectedItemName, setSelectedItemName] = useState('');
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [selectedColor, setSelectedColor] = useState(null);
    const [selectedSize, setSelectedSize] = useState(null);
    const [wishlistItems, setWishlistItems] = useState([]);

    const { filtered } = location.state || {};
    const navigate = useNavigate();

    // Refs for caching and mounting
    const imageCacheRef = useRef(new Map());
    const mountedRef = useRef(true);
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const apiLocalUrl = process.env.REACT_APP_API_LOCAL_URL;
    const userId = useSelector((state) => state.user.id);

    // Cleanup on unmount
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    // Get loginId from localStorage
    useEffect(() => {
        const storedLoginId = localStorage.getItem('loginId');
        setLoginId(storedLoginId);
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Optimized: Check multiple images at once (from Home page)
    const checkImagesBatch = async (imageCodes) => {
        const batchSize = 10;
        const results = {};
        
        for (let i = 0; i < imageCodes.length; i += batchSize) {
            const batch = imageCodes.slice(i, i + batchSize);
            const batchPromises = batch.map(async (code) => {
                if (imageCacheRef.current.has(code)) {
                    return { code, exists: imageCacheRef.current.get(code) };
                }
                
                try {
                    const res = await fetch(`${apiLocalUrl}/check-image/${code}`);
                    const data = await res.json();
                    imageCacheRef.current.set(code, data.exists);
                    return { code, exists: data.exists };
                } catch (err) {
                    console.error("Image check failed:", err);
                    imageCacheRef.current.set(code, false);
                    return { code, exists: false };
                }
            });
            
            const batchResults = await Promise.all(batchPromises);
            batchResults.forEach(result => {
                results[result.code] = result.exists;
            });
            
            if (i + batchSize < imageCodes.length) {
                await new Promise(resolve => setTimeout(resolve, 50));
            }
        }
        
        return results;
    };

    // Optimized: Get available color for a product (from Home page)
    const getAvailableColorForProduct = async (productId) => {
        try {
            const response = await axios.get(`${apiBaseUrl}/getProductDetails/BLACKBATON_2526?Id=${productId}`);
            if (response.status === 200) {
                const data = response.data;
                const distinctColorsArr = [...new Set(
                    data.filter(item => item.ItemId === productId).map(item => item.Color?.trim()).filter(Boolean)
                )];

                const colorCodes = distinctColorsArr.map(color => `${productId}_${color}.webp`);
                const colorResults = await checkImagesBatch(colorCodes);
                
                for (const color of distinctColorsArr) {
                    const code = `${productId}_${color}.webp`;
                    if (colorResults[code]) {
                        return color;
                    }
                }
                return null;
            }
        } catch (error) {
            console.error('Error:', error);
            return null;
        }
    };

    // Optimized: Check if image exists for a product (from Home page)
    const checkImageExists = async (product) => {
        try {
            const imageCodesToCheck = [];
            
            if (product.displayFirst) {
                const displayFirstCode = `${product.displayFirst}.webp`;
                imageCodesToCheck.push(displayFirstCode);
            }
            
            const imageResults = await checkImagesBatch(imageCodesToCheck);
            
            if (product.displayFirst) {
                const displayFirstCode = `${product.displayFirst}.webp`;
                if (imageResults[displayFirstCode]) {
                    return { exists: true, code: displayFirstCode, color: null };
                }
            }

            const color = await getAvailableColorForProduct(product.ID);
            if (color) {
                const colorCode = `${product.ID}_${color}.webp`;
                return { exists: true, code: colorCode, color };
            }

            return { exists: false, code: null, color: null };
        } catch (error) {
            console.error('Error checking image:', error);
            return { exists: false, code: null, color: null };
        }
    };

    // Optimized: Process products in batches (from Home page)
    const processProductsBatch = async (products, batchSize = 5) => {
        const validProducts = [];
        
        for (let i = 0; i < products.length && validProducts.length < targetProductCount; i += batchSize) {
            const batch = products.slice(i, i + batchSize);
            
            const batchPromises = batch.map(async (product) => {
                if (!product || validProducts.some(p => p.ID === product.ID)) {
                    return null;
                }
                
                try {
                    const imageCheck = await checkImageExists(product);
                    if (imageCheck.exists) {
                        return {
                            ...product,
                            displayFirst: imageCheck.code?.replace('.webp', '') || product.displayFirst
                        };
                    }
                } catch (err) {
                    console.error('Error checking product', product?.ID, err);
                }
                return null;
            });
            
            const batchResults = await Promise.all(batchPromises);
            const validBatchProducts = batchResults.filter(Boolean);
            
            validProducts.push(...validBatchProducts);
            
            // Update UI with new products immediately
            if (mountedRef.current && validBatchProducts.length > 0) {
                setAllProducts(prev => {
                    const newProducts = validBatchProducts.filter(newProd => 
                        !prev.some(p => p.ID === newProd.ID)
                    );
                    return [...prev, ...newProducts];
                });
                
                // Stagger visibility for animation
                setTimeout(() => {
                    if (!mountedRef.current) return;
                    setVisibleProducts(prev => {
                        const newIds = validBatchProducts
                            .map(p => p.ID)
                            .filter(id => !prev.includes(id));
                        return [...prev, ...newIds];
                    });
                }, 50);
            }
            
            // Small delay between batches
            if (i + batchSize < products.length && validProducts.length < targetProductCount) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }
        }
        
        return validProducts;
    };

    // Optimized: Fetch products with batched processing (from Home page)
    const fetchProductsWithAvailability = async () => {
        if (!apiBaseUrl) return;
        setLoadingProducts(true);
        setAllProducts([]);
        setVisibleProducts([]);
        imageCacheRef.current.clear();

        try {
            const response = await fetch(
                `${apiBaseUrl}/getProductByFilter/BLACKBATON_2526?filter=${encodeURIComponent(filtered)}`
            );

            if (!response.ok) throw new Error(`API request failed with status ${response.status}`);

            const allData = await response.json();
            const initialProducts = Array.isArray(allData) ? allData : [];

            // Remove duplicates by ID
            const uniqueProducts = initialProducts.filter((product, index, self) =>
                index === self.findIndex(p => p.ID === product.ID)
            );

            // Process products in batches
            await processProductsBatch(uniqueProducts, 8);
            
            if (uniqueProducts.length > 0 && !selectedItemName && mountedRef.current) {
                setSelectedItemName(uniqueProducts[0].ItemName);
            }
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            if (mountedRef.current) setLoadingProducts(false);
        }
    };

    // Fetch products on component mount
    useEffect(() => {
        fetchProductsWithAvailability();
    }, [filtered]);

    // Stable filtered products update
    useEffect(() => {
        if (Array.isArray(allProducts) && mountedRef.current) {
            const filtered = allProducts.filter(
                (product) => product.MRP >= minPrice && product.MRP <= maxPrice
            );
            setFilteredProducts(filtered);
        }
    }, [allProducts, minPrice, maxPrice]);

    // Optimized wishlist functions
    const addToWishlist = async (product) => {
        const apiUrl = `${apiBaseUrl}/wishlist/add/BLACKBATON_2526`;
        const activeUserId = loginId || userId;

        if (!activeUserId) {
            alert("Please login to add items to wishlist");
            return;
        }

        const payload = {
            ID: product.ID,
            ItemName: product.ItemName,
            MRP: product.MRP,
            ledcode: activeUserId,
            Qty: product.Qty,
            uniqueCode: product.UniqueCode
        };

        try {
            const response = await fetch(apiUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                setWishlistItems(prev => [...prev, product]);
            } else {
                alert("Already in Wishlist");
            }
        } catch (error) {
            console.error("Error:", error);
            alert("An error occurred");
        }
    };

    useEffect(() => {
        if (!mountedRef.current) return;
        
        const fetchWishlistItems = async () => {
            try {
                const activeUserId = loginId || userId;
                if (!activeUserId) return;

                const response = await fetch(`${apiBaseUrl}/wishlist/items/BLACKBATON_2526/${activeUserId}`);
                const data = await response.json();

                if (data.success && mountedRef.current) {
                    setWishlistItems(data.items);
                }
            } catch (error) {
                console.error("Error fetching wishlist items:", error);
            }
        };

        fetchWishlistItems();
    }, [loginId, apiBaseUrl, userId]);

    const removeFromWishlist = async (productId) => {
        try {
            const response = await fetch(`${apiBaseUrl}/wishlist/delete/BLACKBATON_2526/${productId}`, {
                method: 'DELETE',
            });

            if (response.ok && mountedRef.current) {
                setWishlistItems(prev => prev.filter(item => item.ID !== productId));
            }
        } catch (error) {
            console.error('Error:', error);
        }
    };

    // Fetch categories and other data
    useEffect(() => {
        if (!mountedRef.current) return;
        
        const fetchInitialData = async () => {
            try {
                const [categoriesRes, subCategoriesRes, colorSizeRes] = await Promise.all([
                    fetch(`${apiBaseUrl}/getCategories/BLACKBATON_2526`),
                    fetch(`${apiBaseUrl}/getSubCategories/BLACKBATON_2526`),
                    fetch(`${apiBaseUrl}/getAllColorSize/BLACKBATON_2526`)
                ]);

                const [categoriesData, subCategoriesData, colorSizeData] = await Promise.all([
                    categoriesRes.json(),
                    subCategoriesRes.json(),
                    colorSizeRes.json()
                ]);

                if (mountedRef.current) {
                    setCategories(categoriesData);
                    setSubCategories(subCategoriesData);
                    
                    const uniqueColors = [...new Set(colorSizeData.map(item => item.Color))];
                    const uniqueSizes = [...new Set(colorSizeData.map(item => item.Size))];
                    setColors(uniqueColors);
                    setSizes(uniqueSizes);
                }
            } catch (error) {
                console.error('Error fetching initial data:', error);
            }
        };

        fetchInitialData();
    }, [apiBaseUrl]);

    // Stable filter handler with batched processing
    const handleFilterAction = async (apiUrl) => {
        if (!mountedRef.current) return;
        
        try {
            setLoadingProducts(true);
            setAllProducts([]);
            setVisibleProducts([]);
            imageCacheRef.current.clear();

            const response = await fetch(apiUrl);
            const data = await response.json();
            
            const uniqueProducts = Array.isArray(data) ? data.filter((product, index, self) =>
                index === self.findIndex(p => p.ID === product.ID)
            ) : [];

            await processProductsBatch(uniqueProducts, 8);
        } catch (error) {
            console.error('Error:', error);
        } finally {
            if (mountedRef.current) {
                setLoadingProducts(false);
            }
        }
    };

    const handleSubCategoryClick = (subCategoryId) => {
        handleFilterAction(
            `${apiBaseUrl}/getProductBySubCategory/BLACKBATON_2526?Id=${subCategoryId}`
        );
    };

    const handleCategoryClick = (categoryId) => {
        handleFilterAction(
            `${apiBaseUrl}/getProductByCategory/BLACKBATON_2526?Id=${categoryId}`
        );
    };

    const handleColorClick = async (color) => {
        if (!mountedRef.current) return;
        
        setSelectedColor(color);
        try {
            setLoadingProducts(true);
            setAllProducts([]);
            setVisibleProducts([]);
            imageCacheRef.current.clear();

            const response = await fetch(`${apiBaseUrl}/getAllColorSize/BLACKBATON_2526`);
            const data = await response.json();
            const filteredItems = data.filter(item => color === item.Color);
            const itemIds = [...new Set(filteredItems.map(item => item.ItemId))];

            const productPromises = itemIds.map(itemId =>
                fetch(`${apiBaseUrl}/getProductById/BLACKBATON_2526?Id=${itemId}`).then(res => res.json())
            );
            const products = await Promise.all(productPromises);
            const flattenedProducts = products.flat();
            
            const uniqueProducts = flattenedProducts.filter((product, index, self) =>
                index === self.findIndex(p => p.ID === product.ID)
            );

            await processProductsBatch(uniqueProducts, 8);
        } catch (error) {
            console.error('Error fetching products by color:', error);
            if (mountedRef.current) {
                setLoadingProducts(false);
            }
        }
    };

    const handleSizeClick = async (size) => {
        if (!mountedRef.current) return;
        
        setSelectedSize(size);
        try {
            setLoadingProducts(true);
            setAllProducts([]);
            setVisibleProducts([]);
            imageCacheRef.current.clear();

            const response = await fetch(`${apiBaseUrl}/getAllColorSize/BLACKBATON_2526`);
            const data = await response.json();
            const filteredItems = data.filter(item => size === item.Size);
            const itemIds = [...new Set(filteredItems.map(item => item.ItemId))];

            const productPromises = itemIds.map(itemId =>
                fetch(`${apiBaseUrl}/getProductById/BLACKBATON_2526?Id=${itemId}`).then(res => res.json())
            );
            const products = await Promise.all(productPromises);
            const flattenedProducts = products.flat();
            
            const uniqueProducts = flattenedProducts.filter((product, index, self) =>
                index === self.findIndex(p => p.ID === product.ID)
            );

            await processProductsBatch(uniqueProducts, 8);
        } catch (error) {
            console.error('Error fetching products by size:', error);
            if (mountedRef.current) {
                setLoadingProducts(false);
            }
        }
    };

    // Helper function to check if product is visible (from Home page)
    const isProductVisible = (productId) => {
        return visibleProducts.includes(productId);
    };

    // Get image URL with fallback (from Home page)
    const getProductImageUrl = (product) => {
        if (product.displayFirst) {
            return `${apiLocalUrl}/uploads/${product.displayFirst}.webp?v=${Date.now()}`;
        }
        return product1;
    };

    // Rest of the component functions...
    const handleMinChange = (e) => {
        const value = Math.min(Number(e.target.value), maxPrice - 10);
        setMinPrice(value);
    };

    const handleMaxChange = (e) => {
        const value = Math.max(Number(e.target.value), minPrice + 10);
        setMaxPrice(value);
    };

    const handleApplyFilters = (filters) => {
        if (filters.subCategory) {
            handleSubCategoryClick(filters.subCategory);
        }
        if (filters.category) {
            handleCategoryClick(filters.category);
        }
        if (filters.color) {
            filters.color.forEach(color => handleColorClick(color));
        }
        if (filters.size) {
            filters.size.forEach(size => handleSizeClick(size));
        }
        if (filters.priceRange) {
            setMinPrice(filters.priceRange.minPrice);
            setMaxPrice(filters.priceRange.maxPrice);
        }
    };

    const toggleFilter = () => setShowFilter(!showFilter);
    const closeFilter = () => setShowFilter(!showFilter);
    const togglePrice = () => setPriceExpanded(!isPriceExpanded);
    const toggleColors = () => setIsColorsExpanded(!isColorsExpanded);
    const toggleSize = () => setIsSizeExpanded(!isSizeExpanded);
    const toggleDress = () => setIsDressExpanded(!isDressExpanded);
    const handleFilterClick = () => setShowFilterMob(true);
    const closeFilterMob = () => setShowFilterMob(false);

    function encodeBase64(text) {
        return btoa(text);
    }

    const fullimage = (id, itemName) => {
        navigate(`/fullimage?I=${encodeBase64(String(id))}&N=${encodeBase64(itemName)}`, { state: { id, itemName } });
    };

    const refresh = () => {
        window.location.reload()
    };

    // Determine which products to display based on filters
    const productsToDisplay = filteredProducts.length > 0 ? filteredProducts : allProducts;

    return (
        <div className='min-h-screen flex flex-col'>
            {isMobile ? <NavbarMob /> : <Navbar />}

            <div className='w-full h-auto lg:px-12 px-3 py-6 flex flex-col lg:gap-12 gap-6 lg:mt-[175px] md:mt-[175px] mt-[120px] pb-12'>
                <div className='flex justify-end items-center'>
                    {!showFilter && (
                        <img src={filter} alt="filter" onClick={toggleFilter} className='cursor-pointer lg:flex hidden' />
                    )}
                    {showFilter && (
                        <div className='h-[30px] w-[10%] rounded-[5px] bg-[black] text-base font-[600] font-montserrat text-[white] flex justify-center items-center cursor-pointer' onClick={refresh}>refresh</div>
                    )}
                    <div className='flex justify-between items-center lg:hidden w-full'>
                        <img src={refreshimg} alt="refreshimg" className='cursor-pointer ' onClick={refresh} />
                        <img src={filter} alt="filter" className='cursor-pointer ' onClick={handleFilterClick} />
                    </div>
                </div>

                <div className='pb-12 flex flex-row gap-5'>
                    {showFilter && (
                        <div className='w-[350px] rounded-[2px] min-h-min max-h-max border-2 border-[#BEBCBD] flex flex-col '>
                            {/* Filter content */}
                            <div className='flex justify-between border-b-2 border-[#BEBCBD] p-5 items-center'>
                                <span className='text-[#807D7E] text-xl font-[400] font-montserrat'>Filter</span>
                                <img src={filter} alt="filter" onClick={closeFilter} className='cursor-pointer' />
                            </div>
                            
                            <div className='flex flex-col border-b-2 border-[#BEBCBD] p-5 items-center gap-2'>
                                {subCategories.map((subCategory) => (
                                    <div key={subCategory.Id} className='flex w-full justify-between items-center cursor-pointer text-[#8A8989] hover:bg-[#e0dede] hover:text-[black]' onClick={() => handleSubCategoryClick(subCategory.Id)}>
                                        <span className='font-[400] text-base font-montserrat'>{subCategory.Name}</span>
                                        <img src={forward} alt="forward" />
                                    </div>
                                ))}
                            </div>

                            <div className="flex justify-between border-b-2 border-[#BEBCBD] p-5 items-center cursor-pointer" onClick={togglePrice}>
                                <span className="text-[#807D7E] text-xl font-[600] font-montserrat">Price</span>
                                <img src={top} alt="top" className={`transform transition-transform duration-300 ${isPriceExpanded ? 'rotate-180' : ''}`} />
                            </div>
                            {isPriceExpanded && (
                                <div className="flex flex-col border-b-2 border-[#BEBCBD] px-5 py-8 items-center gap-8 w-full">
                                    <div className="w-full relative flex items-center ">
                                        <input type="range" min="0" max="1000" step="10" value={minPrice} onChange={handleMinChange} className="absolute z-20 appearance-none w-full h-2 bg-transparent pointer-events-auto" style={{ accentColor: "black" }} />
                                        <input type="range" min="0" max="1000" step="10" value={maxPrice} onChange={handleMaxChange} className="absolute z-20 appearance-none w-full h-2 bg-transparent pointer-events-auto" style={{ accentColor: "black" }} />
                                        <div className="w-full h-1 bg-gray-300 relative">
                                            <div className="absolute h-1 bg-black" style={{ left: `${(minPrice / 1000) * 100}%`, right: `${100 - (maxPrice / 1000) * 100}%` }}></div>
                                        </div>
                                    </div>
                                    <div className="flex justify-between w-full">
                                        <div className="w-[97px] h-[32px] rounded-[8px] border-2 border-[#BEBCBD] flex justify-center items-center text-lg font-[400] font-montserrat">₹{minPrice}</div>
                                        <div className="w-[97px] h-[32px] rounded-[8px] border-2 border-[#BEBCBD] flex justify-center items-center text-lg font-[400] font-montserrat">₹{maxPrice}</div>
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-between border-b-2 border-[#BEBCBD] p-5 items-center cursor-pointer" onClick={toggleColors}>
                                <span className="text-[#807D7E] text-xl font-[600] font-montserrat">Colors</span>
                                <img src={top} alt="top" className={`transform transition-transform duration-300 ${isColorsExpanded ? 'rotate-180' : ''}`} />
                            </div>
                            {isColorsExpanded && (
                                <div className="flex flex-col items-center border-b-2 border-[#BEBCBD] px-5 py-8 gap-4">
                                    <div className='grid grid-cols-4 gap-4'>
                                        {colors.map((color, index) => (
                                            <div key={index} className='flex flex-col gap-2 items-center cursor-pointer' onClick={() => handleColorClick(color)}>
                                                <div className='w-[37px] h-[37px] rounded-[12px] border-2 border-[#F4F1F1]' style={{ backgroundColor: color }}></div>
                                                <span className='text-[#8A8989] text-sm font-[400] font-montserrat'>{color || 'n/a'}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-between border-b-2 border-[#BEBCBD] p-5 items-center cursor-pointer" onClick={toggleSize}>
                                <span className="text-[#807D7E] text-xl font-[600] font-montserrat">Size</span>
                                <img src={top} alt="top" className={`transform transition-transform duration-300 ${isSizeExpanded ? 'rotate-180' : ''}`} />
                            </div>
                            {isSizeExpanded && (
                                <div className="flex flex-col items-center border-b-2 border-[#BEBCBD] px-5 py-8 gap-4">
                                    <div className='grid grid-cols-3 gap-4'>
                                        {sizes.map((size, index) => (
                                            <div key={index} className='flex flex-col gap-2 items-center cursor-pointer' onClick={() => handleSizeClick(size)}>
                                                <div className='w-[61px] h-[32px] rounded-[12px] border-2 border-[#BEBCBD] flex justify-center items-center font-[600] text-sm font-montserrat'>{size}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-between border-b-2 border-[#BEBCBD] p-5 items-center cursor-pointer" onClick={toggleDress}>
                                <span className="text-[#807D7E] text-xl font-[600] font-montserrat">Dress Style</span>
                                <img src={top} alt="top" className={`transform transition-transform duration-300 ${isDressExpanded ? 'rotate-180' : ''}`} />
                            </div>
                            {isDressExpanded && (
                                <div className='flex flex-col p-5 items-center gap-2'>
                                    {categories.map((category) => (
                                        <div key={category.Id} className='flex w-full justify-between items-center cursor-pointer text-[#8A8989] hover:bg-[#e0dede] hover:text-[black]' onClick={() => handleCategoryClick(category.Id)}>
                                            <span className='font-[400] text-base font-montserrat'>{category.Name}</span>
                                            <img src={forward} alt="forward" />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    
                    {/* Products Grid - Using Home page technique */}
                    <div className={`grid w-full gap-5 ${showFilter ? "lg:grid-cols-3 grid-cols-2" : "lg:grid-cols-4 md:grid-cols-3 grid-cols-2"}`}>
                        {loadingProducts && productsToDisplay.length === 0 ? (
                            // Show loading skeletons only when products are still loading
                            Array.from({ length: 12 }).map((_, index) => (
                                <div key={index} className="flex flex-col gap-2">
                                    <div className="lg:h-[382px] md:h-[300px] h-[200px] rounded-[12px] bg-gray-200 animate-pulse"></div>
                                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                                    <div className="h-6 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                                </div>
                            ))
                        ) : productsToDisplay.length > 0 ? (
                            // Show products with progressive visibility animation
                            productsToDisplay.map((product, index) => {
                                const isVisible = isProductVisible(product.ID);

                                return (
                                    <div 
                                        key={product.ID} 
                                        className={`flex flex-col gap-2 cursor-pointer transition-all duration-500 ease-out transform ${
                                            isVisible
                                                ? 'opacity-100 translate-y-0 scale-100'
                                                : 'opacity-0 translate-y-4 scale-95'
                                        }`}
                                        onClick={() => fullimage(product.ID, product.ItemName)}
                                        style={{
                                            transitionDelay: isVisible ? `${index * 50}ms` : '0ms'
                                        }}
                                    >
                                        <div className='lg:h-[382px] md:h-[300px] h-[200px] rounded-[12px] bg-[#EEEEEE] flex items-center justify-center relative overflow-hidden'>
                                            <img 
                                                src={getProductImageUrl(product)}
                                                alt={product.ItemName} 
                                                onError={(e) => { 
                                                    e.target.onerror = null; 
                                                    e.target.src = product1; 
                                                }} 
                                                className='mix-blend-multiply w-full h-full rounded-[12px] object-cover transition-transform duration-500 hover:scale-105' 
                                            />
                                            <div className='absolute top-0 left-0 w-full h-full lg:p-6 p-2 flex justify-end'>
                                                <div 
                                                    className='lg:w-[33px] w-[23px] lg:h-[33px] h-[23px] rounded-full bg-[white] flex justify-center items-center cursor-pointer'
                                                    onClick={(e) => { 
                                                        e.stopPropagation(); 
                                                        wishlistItems.some(item => item.ID === product.ID) 
                                                            ? removeFromWishlist(product.ID) 
                                                            : addToWishlist(product); 
                                                    }}
                                                >
                                                    <img 
                                                        src={wishlistItems.some(item => item.ID === product.ID) ? filledHeart : heart} 
                                                        alt="heart" 
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className='flex justify-between w-full'>
                                            <div className='flex flex-col gap-1 w-full'>
                                                <span className='lg:text-base text-xs font-[600] font-montserrat text-left'>{product.ItemName}</span>
                                            </div>
                                            <div className='flex flex-col gap-1 items-end'>
                                                <div className='lg:w-[84px] lg:h-[37px] w-[70px] h-[30px] rounded-[8px] text-red-600 bg-[#F6F6F6] flex justify-center items-center text-sm font-[700] font-montserrat'>
                                                    ₹{product.MRP}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            // Show "no products" message
                            <div className="col-span-full flex justify-center items-center py-12">
                                <span className="text-gray-500 text-lg">No products found.</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {showFilterMob && <Filter closeFilterMob={closeFilterMob} onApply={handleApplyFilters} />}
            
            {isTab ? <FooterMob /> : <Footer />}
        </div>
    )
}

export default SearchPage